import React, { useRef, useState } from 'react';
import { importAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Upload, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const IMPORT_TYPES = [
  { key: 'candidats',       label: 'Candidats Pipeline',  desc: 'Export Boond « Candidats »'     },
  { key: 'besoins',         label: 'Besoins',             desc: 'Export Boond « Besoins »'        },
  { key: 'societes',        label: 'Sociétés',            desc: 'Export Boond « Sociétés »'       },
  { key: 'contacts',        label: 'Contacts CRM',        desc: 'Export Boond « Contacts »'       },
  { key: 'actions',         label: 'Actions CRM',         desc: 'Export Boond « Actions »'        },
  { key: 'ressources',      label: 'Ressources',          desc: 'Export Boond « Ressources »'     },
  { key: 'projets',         label: 'Projets',             desc: 'Export Boond « Projets »'        },
  { key: 'prestations',     label: 'Prestations',         desc: 'Export Boond « Prestations »'    },
  { key: 'positionnements', label: 'Positionnements',     desc: 'Export Boond « Positionnements »'},
];

function ImportCard({ type }) {
  const inputRef = useRef(null);
  const [file,    setFile]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.endsWith('.csv')) { toast.error('Fichier CSV uniquement'); return; }
    setFile(f);
    setResult(null);
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await importAPI.upload(type.key, form);
      setResult({ ok: true, ...r.data });
      toast.success(`Import ${type.label} terminé`);
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Erreur';
      setResult({ ok: false, error: msg });
      toast.error(`Erreur : ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 12, padding: '20px 24px',
      background: 'var(--surface)', display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{type.label}</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>{type.desc}</div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFile}
      />

      {!file ? (
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => inputRef.current?.click()}
          style={{ alignSelf: 'flex-start' }}
        >
          <Upload size={13}/> Choisir un fichier CSV
        </button>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: '0.8rem', background: 'var(--surface-alt, rgba(255,255,255,0.05))',
            border: '1px solid var(--border)', borderRadius: 6, padding: '3px 10px',
            maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {file.name}
          </span>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleImport}
            disabled={loading}
            style={{ minWidth: 90 }}
          >
            {loading ? '…' : <><Upload size={13}/> Importer</>}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => { setFile(null); setResult(null); if (inputRef.current) inputRef.current.value = ''; }}
            disabled={loading}
          >
            ✕
          </button>
        </div>
      )}

      {result && (
        <div style={{
          fontSize: '0.8rem', borderRadius: 8, padding: '8px 12px',
          background: result.ok ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
          border: `1px solid ${result.ok ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`,
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          {result.ok
            ? <CheckCircle size={14} style={{ color: '#22c55e', marginTop: 1, flexShrink: 0 }}/>
            : <XCircle    size={14} style={{ color: '#ef4444', marginTop: 1, flexShrink: 0 }}/>
          }
          {result.ok ? (
            <span>
              <strong>{result.inserted ?? 0}</strong> insérés ·{' '}
              <strong>{result.updated  ?? 0}</strong> mis à jour ·{' '}
              <strong>{result.skipped  ?? 0}</strong> ignorés
              {result.errors > 0 && <> · <span style={{ color: '#f59e0b' }}>{result.errors} erreurs</span></>}
            </span>
          ) : (
            <span style={{ color: '#ef4444' }}>{result.error}</span>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminImport() {
  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1 style={{ margin: 0 }}>Import CSV Boond</h1>
          <p style={{ margin: 0 }}>Importer ou mettre à jour les données depuis un export Boond (Super Admin uniquement)</p>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: 16,
      }}>
        {IMPORT_TYPES.map(t => <ImportCard key={t.key} type={t}/>)}
      </div>

      <div style={{
        marginTop: 24, padding: '12px 16px', borderRadius: 10,
        background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
        display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '0.82rem',
      }}>
        <AlertCircle size={15} style={{ color: '#f59e0b', marginTop: 1, flexShrink: 0 }}/>
        <span>
          L'import est idempotent : les enregistrements existants (identifiés par leur référence Boond) sont mis à jour,
          les nouveaux sont insérés. Aucun doublon ne sera créé.
        </span>
      </div>
    </div>
  );
}
